import { z } from 'zod';
import { paginated, paginationQuerySchema } from './common.js';
/**
 * Exactly what the provider sent, for the tenant that received it.
 *
 * `RawProviderEvent.payload` is documented in the schema as *"original provider
 * payload, never rewritten"*, and it has been written since ingestion existed
 * with **nothing able to read it** — no route, no repository read, no screen. A
 * record kept for audit that cannot be produced on request is not an audit
 * trail; it is storage.
 *
 * This is deliberately the OPPOSITE of what every other screen in this
 * programme has been moving towards. Elsewhere a payload on screen is a fact
 * wearing a costume and gets read back as prose. Here the bytes ARE the
 * artifact: the question is "what did they actually send us", and a summary —
 * however faithful — cannot answer it. So the payload is shown verbatim,
 * unformatted beyond indentation, and never interpreted.
 *
 * **The tenant/platform split is the same one `eventProcessingHealthSchema`
 * draws, and for the same reason.** The payload is the tenant's own data and is
 * theirs to read. `processingError` is not:
 *
 *  - **`UNRESOLVED_REFERENCE`** is a DELIBERATE refusal whose text was written
 *    for a reader — "geofence 'Depot North' could not be resolved for this
 *    tenant". Shown.
 *  - **`FAILED`** is an unhandled exception's `(err as Error).message`, which
 *    can carry an internal id, a constraint name or a fact about another row.
 *    The STATUS is shown, because a tenant whose events are failing must know;
 *    the text is withheld and named as the platform's to fix.
 *
 * Enforced in the repository rather than here, so no later caller can ask for
 * one tenant's raw failure text and get it.
 */
/**
 * Statuses a raw event can hold, mirroring the column comment on
 * `RawProviderEvent.processingStatus`.
 *
 * A plain string on READ deliberately: rows written before a status was added
 * must still render. An unrecognised value is a row that is visibly odd rather
 * than invisible — the same rule a policy's `eventType` follows.
 */
export const INBOUND_EVENT_STATUSES = [
    'RECEIVED',
    'QUEUE_PENDING',
    'QUEUED',
    'PROCESSING',
    'PROCESSED',
    'FAILED',
    'UNRESOLVED_REFERENCE',
];
export const inboundEventListItemSchema = z.object({
    id: z.string().uuid(),
    /** The provider's own id for this event — what a customer quotes at us. */
    providerEventId: z.string(),
    providerId: z.string(),
    /** The event type EXACTLY as received, before any mapping. */
    eventTypeRaw: z.string(),
    /** The provider's timestamp, as received. */
    occurredAt: z.string(),
    /** When OIP took delivery. Different from `occurredAt`, sometimes by a lot. */
    receivedAt: z.string(),
    processingStatus: z.string(),
    /**
     * Why it was refused, for a DELIBERATE refusal only. Null for every other
     * status, `FAILED` included — see the note above.
     */
    refusalReason: z.string().nullable(),
    /**
     * True when this row failed with an exception. The count matters to the
     * tenant; the text is not theirs to read.
     */
    platformFailure: z.boolean(),
    /** How many canonical events this payload became. Zero is a real answer. */
    canonicalEventCount: z.number().int(),
    correlationId: z.string().nullable(),
});
export const inboundEventDetailSchema = inboundEventListItemSchema.extend({
    /**
     * The payload as received, verbatim. The whole point of the endpoint.
     *
     * `unknown` rather than a shape: OIP does not own it, every provider differs,
     * and validating it here would be a second place that has to know what a
     * provider sends. It is stored unrewritten and returned unrewritten.
     */
    payload: z.unknown(),
    schemaVersion: z.number().int(),
    /** The canonical events this payload produced, for following the trail on. */
    canonicalEvents: z.array(z.object({
        id: z.string().uuid(),
        eventType: z.string(),
        occurredAt: z.string(),
        /** Null when the event named a vehicle this tenant does not hold (D12). */
        vehicleId: z.string().uuid().nullable(),
        vehicleRegistration: z.string().nullable(),
    })),
});
/**
 * `.strict()`, so a misspelled filter is a 400 rather than a silently wider
 * result set — the defect `auditListQuerySchema` had, found in six endpoints.
 */
export const inboundEventQuerySchema = paginationQuerySchema
    .extend({
    /** Exact match on the provider's id: the audit question is "this event". */
    providerEventId: z.string().min(1).optional(),
    providerId: z.string().min(1).optional(),
    eventTypeRaw: z.string().min(1).optional(),
    processingStatus: z.enum(INBOUND_EVENT_STATUSES).optional(),
    /** Inclusive window on `receivedAt` — when WE took delivery, not when it happened. */
    receivedFrom: z.string().datetime().optional(),
    receivedTo: z.string().datetime().optional(),
})
    .strict();
export const inboundEventListResponseSchema = paginated(inboundEventListItemSchema);
//# sourceMappingURL=inbound-event.js.map